import React from "react";
import { SizeAndFit } from "../../../types";
import Image from "next/image";
import { cn } from "@/lib/tailwind";
import { PdpContent } from "@/common/lib/types";
import { getCloudinaryURL } from "@/utils/cloudinary";
import NoImageSrc from "@/assets/images/no_image.svg";
import CldImage from "@/components/ui/cldImg/CldImage";
import DummyClientConsoleComp from "@/components/DummyClientConsoleComp";
import { useParams } from "next/navigation";
import { getLangFromLocale } from "@/apiUtils/commonUtils";

type IProps = {
  images: string[];
  lensSize: SizeField;
  tSize: SizeField;
  bridgeSize: SizeField;
  currentFit: string;
  layoutDetails: PdpContent;
} & SizeAndFit["data"]["measurementsTab"];

type SizeField = {
  label: string;
  value: string;
};

const MeasurementTab = ({
  productMeasurements,
  fit,
  images,
  assetCode,
  layoutDetails,
  ...rest
}: IProps) => {
  const requiredItems = ["lensSize", "tSize", "bridgeSize"];
  const { locale } = useParams();
  const lang = getLangFromLocale(locale as string);
  const textAlignment = lang === "en"
      ? "lg:text-left"
      : "lg:text-right";

  return (
    <>
      <div className="flex flex-1 flex-col gap-6 pt-10 lg:pt-0">
        <div className="">
          <h2 className={`text-center text-lg font-semibold ${textAlignment}`}>
            {productMeasurements?.title}
          </h2>
          <div className="flex flex-wrap justify-center gap-10 py-1 lg:justify-start">
            {productMeasurements?.items?.map(
              (item, idx) =>
                requiredItems?.includes(item?.attributeCode!) && (
                  <div key={idx} className="flex flex-col items-center justify-center">
                    <Image
                      src={item?.media!}
                      alt="img"
                      width={200}
                      height={200}
                      className="h-20 w-20"
                    />
                    <p className="text-sm text-tertiary-6">
                      {(rest as any)?.[item?.attributeCode!]?.label ?? ""}
                    </p>
                    <p className="text-sm">{(rest as any)?.[item?.attributeCode!]?.value ?? ""}</p>
                  </div>
                ),
            )}
          </div>
        </div>
        <div className="pt-5">
          <h2 className={`text-center text-lg font-semibold ${textAlignment}`}>
            {layoutDetails?.translations?.common?.common_variant_attribute_fit_label}
          </h2>
          <div className="flex justify-center gap-7 py-5 lg:justify-start">
            {fit?.items?.map((item, idx) => (
              <div
                key={idx}
                className={cn(
                  "flex flex-col items-center justify-center p-3.5",
                  item?.type === rest.currentFit ? "border border-black" : "",
                )}
              >
                <DummyClientConsoleComp data={item} messagePrefix="Inside Fit Tab"/>
                <CldImage
                  src={item?.media ? getCloudinaryURL(item?.media) : NoImageSrc.src}
                  alt=""
                  width={200}
                  height={200}
                  className="h-12 w-12"
                />
                <p className="pt-2 text-sm text-tertiary-6">{item?.title}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="flex flex-1 items-center justify-center gap-5">
        <CldImage
          src={images[assetCode] ? getCloudinaryURL(images[assetCode]) : NoImageSrc}
          alt="img"
          width={708}
          height={608}
          className="w-full invert-[2%]"
        />
      </div>
    </>
  );
};

export default MeasurementTab;
